/** @typedef {'invalid' | 'empty' | 'ok'} NormalizeEditorLinkResultKind */

/**
 * Blocks dangerous URL schemes in user-provided link targets.
 * @param {string} href
 * @returns {boolean}
 */
export function isUnsafeEditorLinkHref(href) {
  if (!href || typeof href !== 'string') {
    return true;
  }

  return /^(javascript|data|vbscript):/i.test(href.trim());
}

/**
 * Whether a normalized href is allowed in the editor Link extension.
 * @param {string} href
 * @returns {boolean}
 */
export function isValidEditorLinkHref(href) {
  if (!href || typeof href !== 'string') {
    return false;
  }

  const trimmed = href.trim();

  if (!trimmed || isUnsafeEditorLinkHref(trimmed)) {
    return false;
  }

  if (trimmed.startsWith('mailto:')) {
    return /^mailto:[^\s<>"']+$/i.test(trimmed);
  }

  if (trimmed.startsWith('/')) {
    return trimmed.length > 1 && !trimmed.startsWith('//');
  }

  return /^https?:\/\//i.test(trimmed);
}

/**
 * Normalizes user input into an href for TipTap `setLink`, or `null` when invalid.
 * @param {string} input
 * @returns {string | null}
 */
export function normalizeEditorLinkUrl(input) {
  const raw = (input ?? '').trim();

  if (!raw) {
    return '';
  }

  if (isUnsafeEditorLinkHref(raw)) {
    return null;
  }

  if (raw.startsWith('mailto:')) {
    return isValidEditorLinkHref(raw) ? raw : null;
  }

  if (raw.startsWith('/')) {
    return isValidEditorLinkHref(raw) ? raw : null;
  }

  if (/^https?:\/\//i.test(raw)) {
    return isValidEditorLinkHref(raw) ? raw : null;
  }

  const withProtocol = `https://${raw}`;

  return isValidEditorLinkHref(withProtocol) ? withProtocol : null;
}
